import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";

/** One of an ads account's per-marketplace identifiers: an entity ID or a profile ID. */
const alternateIdSchema = z.object({
  countryCode: z.string(),
  entityId: z.string().optional(),
  // Amazon sends profile IDs as numbers in some payloads and strings in others; compare them as strings.
  profileId: z.union([z.string(), z.number()]).transform(String).optional(),
});

const adsAccountSchema = z
  .object({
    adsAccountId: z.string(),
    accountName: z.string().nullish(),
    countryCodes: z.array(z.string()).nullish(),
    alternateIds: z.array(alternateIdSchema).nullish(),
  })
  // Amazon lists both kinds of ID in one array; split them by kind.
  .transform(({ alternateIds, ...account }) => ({
    ...account,
    entityIds: (alternateIds ?? []).flatMap(({ countryCode, entityId: id }) => (id ? [{ countryCode, id }] : [])),
    profileIds: (alternateIds ?? []).flatMap(({ countryCode, profileId: id }) => (id ? [{ countryCode, id }] : [])),
  }));

export type AdsAccount = z.infer<typeof adsAccountSchema>;
/** An entity or profile ID, with the marketplace it belongs to. */
export type CountryId = AdsAccount["entityIds"][number];

export const advertisingAccountsApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    // A pass-through of Amazon Ads' `POST /adsAccounts/list` response.
    alias: "getAdvertisingAccounts",
    method: "get",
    path: "/advertising_accounts/",
    parameters: [{ name: "marketplace", type: "Query", schema: z.string() }],
    status: 200,
    response: z.object({
      adsAccounts: z.array(adsAccountSchema),
      nextToken: z.string().nullish(),
    }),
  }).build(),
);
